import { useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetPlanVsActualQuery, useGetCostPivotQuery } from "@api/pApi";
import { fmtMoney, fmtPct, fmtSignedMoney } from "@lib/format";
import type { CostPivotDimension, PhaseVariance } from "@api/types";
import type { MessageKey } from "@i18n/messages";

const DIMENSIONS: { id: CostPivotDimension; key: MessageKey }[] = [
  { id: "vendor", key: "cost.pivot.vendor" },
  { id: "phase", key: "cost.pivot.phase" },
  { id: "cost_class", key: "cost.pivot.costClass" },
];

/**
 * Cost & AFE — plan-vs-actual (from /plan-vs-actual) with a by-phase variance
 * chart, plus a by-vendor / by-phase / by-cost-class pivot (from /cost-pivot).
 * Every number is server-computed by Oxbo-P; the FE only formats and lays out.
 */
export function CostPanel({ eventId }: { eventId: string }) {
  const { t, locale } = useLocale();
  const [dimension, setDimension] = useState<CostPivotDimension>("vendor");

  const {
    data: pva,
    isLoading,
    isError,
    refetch,
  } = useGetPlanVsActualQuery(eventId, { skip: !eventId });
  const { data: pivot } = useGetCostPivotQuery({ eventId, dimension }, { skip: !eventId });

  const pctSpent = pva && pva.afe_total > 0 ? (pva.actual_total / pva.afe_total) * 100 : null;
  const stats: Stat[] = pva
    ? [
        { label: t("cost.kpi.afeTotal"), value: fmtMoney(pva.afe_total, locale, { compact: true }) },
        { label: t("cost.kpi.actual"), value: fmtMoney(pva.actual_total, locale, { compact: true }), tone: "ops" },
        {
          label: t("cost.kpi.variance"),
          value: fmtSignedMoney(pva.variance, locale),
          tone: pva.variance > 0 ? "crit" : "good",
        },
        { label: t("cost.kpi.pctSpent"), value: fmtPct(pctSpent, locale, 1) },
      ]
    : [];

  // Bar scale: widest of AFE/actual across phases anchors 100%.
  const maxVal = Math.max(1, ...(pva?.by_phase ?? []).flatMap((p) => [p.afe, p.actual]));

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!pva && pva.by_phase.length === 0}
      emptyMessage={t("cost.noData")}
      onRetry={refetch}
    >
      {stats.length ? <StatBand stats={stats} /> : null}

      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("cost.byPhase")}</h3>
        <div className="barviz">
          {(pva?.by_phase ?? []).map((p) => (
            <PhaseBar key={p.phase} phase={p} maxVal={maxVal} locale={locale} />
          ))}
        </div>
        <div className="barviz-legend">
          <span><i className="swatch plan" /> {t("cost.legend.plan")}</span>
          <span><i className="swatch actual" /> {t("cost.legend.actual")}</span>
          <span><i className="swatch over" /> {t("cost.legend.over")}</span>
        </div>
      </div>

      {/* variance table */}
      <div className="card" style={{ marginTop: 14 }}>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("cost.col.phase")}</th>
                <th className="num">{t("cost.col.afe")}</th>
                <th className="num">{t("cost.col.actual")}</th>
                <th className="num">{t("cost.col.variance")}</th>
                <th className="num">{t("cost.col.variancePct")}</th>
              </tr>
            </thead>
            <tbody>
              {(pva?.by_phase ?? []).map((p) => {
                const pct = p.afe ? (p.variance / p.afe) * 100 : 0;
                const over = p.variance > 0;
                return (
                  <tr key={p.phase} data-owner="p">
                    <td>{p.phase}</td>
                    <td className="num tab-nums">{fmtMoney(p.afe, locale)}</td>
                    <td className="num tab-nums">{fmtMoney(p.actual, locale)}</td>
                    <td className={`num tab-nums ${over ? "var-bad" : "var-good"}`}>
                      {fmtSignedMoney(p.variance, locale)}
                    </td>
                    <td className={`num tab-nums ${over ? "var-bad" : "var-good"}`}>
                      {fmtPct(pct, locale, 1)}
                    </td>
                  </tr>
                );
              })}
              {pva ? (
                <tr className="total-row" data-owner="p">
                  <td>{t("cost.total")}</td>
                  <td className="num tab-nums">{fmtMoney(pva.afe_total, locale)}</td>
                  <td className="num tab-nums">{fmtMoney(pva.actual_total, locale)}</td>
                  <td className={`num tab-nums ${pva.variance > 0 ? "var-bad" : "var-good"}`}>
                    {fmtSignedMoney(pva.variance, locale)}
                  </td>
                  <td className="num" />
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* pivot */}
      <div className="card" style={{ marginTop: 14 }}>
        <div className="panel-head-row">
          <h3>{t("cost.pivot")}</h3>
          <div className="seg" role="group" aria-label={t("cost.pivot")}>
            {DIMENSIONS.map((d) => (
              <button
                key={d.id}
                type="button"
                aria-pressed={dimension === d.id}
                className={dimension === d.id ? "active" : ""}
                onClick={() => setDimension(d.id)}
              >
                {t(d.key)}
              </button>
            ))}
          </div>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("cost.col.key")}</th>
                <th className="num">{t("cost.col.total")}</th>
              </tr>
            </thead>
            <tbody>
              {(pivot?.rows ?? []).map((r) => (
                <tr key={r.key} data-owner="p">
                  <td>{r.key}</td>
                  <td className="num tab-nums">{fmtMoney(r.total, locale)}</td>
                </tr>
              ))}
              {pivot ? (
                <tr className="total-row" data-owner="p">
                  <td>{t("cost.total")}</td>
                  <td className="num tab-nums">{fmtMoney(pivot.total, locale)}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </QueryState>
  );
}

function PhaseBar({
  phase,
  maxVal,
  locale,
}: {
  phase: PhaseVariance;
  maxVal: number;
  locale: ReturnType<typeof useLocale>["locale"];
}) {
  const planW = (phase.afe / maxVal) * 100;
  const actualW = (phase.actual / maxVal) * 100;
  const over = phase.actual > phase.afe;
  return (
    <div className="barrow">
      <span className="barlabel">{phase.phase}</span>
      <div className="bartrack">
        <div className="barfill plan" style={{ width: `${planW}%` }} />
        <div className={`barfill actual${over ? " over" : ""}`} style={{ width: `${actualW}%` }} />
      </div>
      <span className="baramt mono">{fmtMoney(phase.actual, locale, { compact: true })}</span>
    </div>
  );
}
